import React, { useState, useEffect } from 'react';
import { stock } from '../data/media';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const links = [
    { to: '/', label: 'HOME' },
    { to: '/groups', label: 'OUR VERTICALS' },
    { to: '/announcements', label: 'ANNOUNCEMENTS' },
    { to: '/about', label: 'ABOUT US' },
    { to: '/contact', label: 'CONTACT' },
  ];

  const isActive = (to: string) => location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled
        ? 'linear-gradient(135deg, #00464f 0%, #005B66 55%, #0B7078 100%)'
        : 'linear-gradient(135deg, #005B66 0%, #0B7078 100%)',
      backdropFilter: 'blur(16px) saturate(160%)',
      WebkitBackdropFilter: 'blur(16px) saturate(160%)',
      boxShadow: scrolled ? '0 8px 32px rgba(0,20,22,0.28)' : '0 2px 12px rgba(0,20,22,0.12)',
      transition: 'background 0.4s ease, box-shadow 0.4s ease',
    }}>
      {/* Thin accent line along the very top — a quiet nod to the brand red */}
      <div style={{ height: 3, background: 'linear-gradient(90deg, #D62828, #f04e4e, #D62828)', backgroundSize: '200% 100%', animation: 'navAccentFlow 6s linear infinite' }} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
        <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <motion.img
              src={stock.mainlogo} alt="Reach Groups Logo" width={50} height={40}
              whileHover={{ scale: 1.08, rotate: -4 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              style={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.25))' }}
            />
            <div>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', letterSpacing: '0.3px', lineHeight: 1.1 }}>REACH GROUPS</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: '1.5px', fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>ONE REACH ENDLESS CARE</div>
            </div>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="nav-links-desktop"
        >
          {links.map(l => (
            <Link key={l.to} to={l.to} className="nav-link-anim" style={{
              position: 'relative', padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
              color: isActive(l.to) ? '#fff' : 'rgba(255,255,255,0.72)', fontFamily: 'Poppins, sans-serif', letterSpacing: '0.4px',
            }}>
              {l.label}
              {isActive(l.to) && (
                <motion.div layoutId="nav-pill" style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.16)', borderRadius: 8, zIndex: -1 }} transition={{ type: 'spring', stiffness: 400, damping: 32 }} />
              )}
            </Link>
          ))}
        </motion.div>

        <motion.button
          onClick={() => setMenuOpen(!menuOpen)}
          whileTap={{ scale: 0.9 }}
          style={{ display: 'none', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: 6, color: '#fff', cursor: 'pointer', alignItems: 'center', justifyContent: 'center' }}
          className="nav-hamburger"
          aria-label="Toggle menu">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={menuOpen ? 'close' : 'open'}
              initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }} style={{ display: 'flex' }}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            style={{ overflow: 'hidden', background: 'linear-gradient(160deg, #00464f, #0B7078)', borderTop: '1px solid rgba(255,255,255,0.12)' }}
          >
            <motion.div
              initial="closed" animate="open" exit="closed"
              variants={{ open: { transition: { staggerChildren: 0.05 } }, closed: {} }}
              style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              {links.map(l => (
                <motion.div key={l.to} variants={{ open: { opacity: 1, x: 0 }, closed: { opacity: 0, x: -16 } }} transition={{ duration: 0.25 }}>
                  <Link to={l.to} style={{
                    display: 'block', padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
                    color: '#fff',
                    background: isActive(l.to) ? 'rgba(255,255,255,0.16)' : 'transparent',
                  }}>{l.label}</Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes navAccentFlow { 0% { background-position: 0% 0; } 100% { background-position: -200% 0; } }
        .nav-link-anim { overflow: hidden; }
        .nav-link-anim::after {
          content: ''; position: absolute; left: 16px; right: 16px; bottom: 5px; height: 2px;
          background: #fff; transform: scaleX(0); transform-origin: left; transition: transform 0.3s cubic-bezier(.22,1,.36,1);
        }
        .nav-link-anim:hover::after { transform: scaleX(1); }
        .nav-link-anim:hover { color: #fff !important; }
        @media (max-width: 900px) {
          .nav-links-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;